/**
 * 전체 메뉴 패널
 * - 데스크톱(≥768): 서브메뉴를 메인 컬럼 오른쪽(gap 90px)에 절대 배치
 * - 모바일(<768): 오른쪽 슬라이드 패널 + 아코디언 2차 메뉴
 * - 열림 시 body 스크롤 잠금
 */
const MOBILE_MQ = '(max-width: 767px)';

export function initMenu(root: ParentNode): () => void {
  const drawer = root.querySelector<HTMLElement>('[data-nav-drawer]');
  const openBtn = root.querySelector<HTMLButtonElement>('[data-menu-open]');
  const closeTargets = root.querySelectorAll<HTMLElement>('[data-menu-close]');
  const mainButtons = root.querySelectorAll<HTMLButtonElement>('[data-menu-main]');
  const subPanels = root.querySelectorAll<HTMLElement>('[data-menu-sub]');
  const menu = drawer?.querySelector<HTMLElement>('.nav-drawer__menu');

  if (!drawer || !openBtn || !menu) return () => undefined;

  const SUB_GAP = 90;
  const SUB_TOP_OFFSET = 8;
  const mobileMq = window.matchMedia(MOBILE_MQ);
  let lockedScrollY = 0;

  const isMobile = () => mobileMq.matches;

  const hideAllSubs = () => {
    mainButtons.forEach((btn) => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-expanded', 'false');
    });
    subPanels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.hidden = true;
      panel.style.top = '';
      panel.style.left = '';
    });
  };

  const lockScroll = (lock: boolean) => {
    if (lock) {
      lockedScrollY = window.scrollY;
      document.body.classList.add('is-nav-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return;
    }

    document.body.classList.remove('is-nav-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, lockedScrollY);
  };

  const setOpen = (open: boolean) => {
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    openBtn.setAttribute('aria-expanded', String(open));
    lockScroll(open);
    if (open) hideAllSubs();
  };

  const positionSub = (panel: HTMLElement, mainBtn: HTMLButtonElement) => {
    const primary = menu.querySelector<HTMLElement>('.nav-drawer__primary');
    if (!primary) return;

    const primaryRect = primary.getBoundingClientRect();
    const btnRect = mainBtn.getBoundingClientRect();
    const top = btnRect.top - primaryRect.top + SUB_TOP_OFFSET;
    panel.style.left = `${primaryRect.width + SUB_GAP}px`;
    panel.style.top = `${top}px`;
  };

  const activateMain = (key: string, mainBtn: HTMLButtonElement) => {
    if (isMobile() && mainBtn.classList.contains('is-active')) {
      hideAllSubs();
      return;
    }

    mainButtons.forEach((btn) => {
      const active = btn.dataset.menuMain === key;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-expanded', String(active));
    });

    subPanels.forEach((panel) => {
      const active = panel.dataset.menuSub === key;
      const hasItems = panel.querySelectorAll(':scope > li').length > 0;
      const show = active && hasItems;
      panel.classList.toggle('is-active', show);
      panel.hidden = !show;

      if (show && !isMobile()) {
        positionSub(panel, mainBtn);
      } else {
        panel.style.top = '';
        panel.style.left = '';
      }
    });
  };

  const onOpen = () => setOpen(true);

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') setOpen(false);
  };

  const onMainClick = (event: Event) => {
    const btn = event.currentTarget as HTMLButtonElement;
    const key = btn.dataset.menuMain;
    if (!key) return;
    activateMain(key, btn);
  };

  const onBackdropClick = (event: MouseEvent) => {
    if (!isMobile() || !drawer.classList.contains('is-open')) return;
    if (event.target === drawer) setOpen(false);
  };

  const onResize = () => {
    const activeBtn = drawer.querySelector<HTMLButtonElement>('.nav-drawer__main.is-active');
    const activePanel = drawer.querySelector<HTMLElement>('.nav-drawer__secondary.is-active');
    if (!activeBtn || !activePanel || activePanel.hidden) return;

    if (isMobile()) {
      activePanel.style.top = '';
      activePanel.style.left = '';
      return;
    }

    positionSub(activePanel, activeBtn);
  };

  openBtn.addEventListener('click', onOpen);
  closeTargets.forEach((el) => {
    el.addEventListener('click', () => setOpen(false));
  });
  mainButtons.forEach((btn) => btn.addEventListener('click', onMainClick));
  drawer.addEventListener('click', onBackdropClick);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', onResize);
  mobileMq.addEventListener('change', onResize);

  hideAllSubs();

  return () => {
    openBtn.removeEventListener('click', onOpen);
    mainButtons.forEach((btn) => btn.removeEventListener('click', onMainClick));
    drawer.removeEventListener('click', onBackdropClick);
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('resize', onResize);
    mobileMq.removeEventListener('change', onResize);
    lockScroll(false);
  };
}
